/**
 * D344: a small top-down sketch of the land a seed gives, for the first run's Shuffle: the
 * outline, the fields, the yard, the pond, the trees and the river, and the land's kind and
 * traits under it. Drawn from the same layout the world will be made with, so what the player
 * sees here is the world they get.
 */
import { useMemo } from 'preact/hooks';
import { getIdleContent } from '@hedgerow/data';
import {
  hash32,
  idleLandTraitIds,
  idleLandTraitNames,
  idleLayoutFor,
  normaliseMapSeed,
} from '@hedgerow/sim';
import { worldOutline } from '../ui/iso.ts';
import { mapKitFor } from '../ui/iso-kit.ts';

/** The fill for each kind of plot, from the app's own tokens. */
const FILL: Record<string, string> = {
  field: 'var(--field-2)',
  pasture: 'var(--field-1)',
  orchard: 'var(--field-3)',
  meadow: 'var(--field-4)',
  pond: 'var(--water)',
};

/** D359: `theme` is the theme the world will be made in; its own kinds and trait names. */
export function LandSketch({ seed, theme }: { seed: string; theme?: string }) {
  const content = getIdleContent(theme);
  const kit = mapKitFor(theme);
  const lay = useMemo(
    () => idleLayoutFor(content, hash32(normaliseMapSeed(seed))),
    [seed, content],
  );
  const traits = idleLandTraitNames(content, idleLandTraitIds(lay)).join(', ');
  const M = 2;
  const x0 = -M,
    y0 = -1,
    w = lay.width + 2 * M,
    h = lay.height + M + 1;
  const { slabs } = worldOutline({ x: 0, y: 0, w: lay.width, h: lay.height, cuts: lay.cuts ?? [] });
  // D348: a theme with no water outside the fence (compute) draws no river.
  const river =
    content.map.terrain.water === 'none'
      ? ''
      : (lay.river ?? lay.stream).map((t) => `${t.x + 0.5},${t.y + 0.5}`).join(' ');
  const inset = (r: { x: number; y: number; w: number; h: number }) => ({
    x: r.x + 0.08,
    y: r.y + 0.08,
    width: r.w - 0.16,
    height: r.h - 0.16,
  });
  return (
    <div style={{ display: 'grid', gap: 6, justifyItems: 'center' }}>
      <svg
        data-testid="first-run-sketch"
        role="img"
        aria-label={traits ? `A sketch of the land: ${traits}` : 'A sketch of the land'}
        viewBox={`${x0} ${y0} ${w} ${h}`}
        style={{ width: '100%', maxWidth: 220, height: 'auto', maxHeight: 200, display: 'block' }}
      >
        {slabs.map((s, i) => (
          <rect key={`s${i}`} x={s.x} y={s.y} width={s.w} height={s.h} fill="var(--green-bg)" />
        ))}
        <rect {...inset(lay.yard)} fill="var(--line)" />
        {Object.entries(lay.plots).map(([id, r]) => (
          <rect key={id} {...inset(r)} rx={0.2} fill={FILL[kit.plots[id]?.kind ?? 'field']} />
        ))}
        {lay.pond && <rect {...inset(lay.pond)} rx={0.6} fill="var(--water)" />}
        {lay.trees.map((t) => (
          <circle
            key={`${t.x},${t.y}`}
            cx={t.x + 0.5}
            cy={t.y + 0.5}
            r={0.42}
            fill="var(--green)"
          />
        ))}
        {river && (
          <polyline
            points={river}
            fill="none"
            stroke="var(--water)"
            stroke-width={0.7}
            stroke-linejoin="round"
            stroke-linecap="round"
          />
        )}
      </svg>
      {traits && (
        <p
          data-testid="first-run-traits"
          style={{ margin: 0, font: '600 14px/1.4 var(--font-ui)', color: 'var(--ink)' }}
        >
          {traits}
        </p>
      )}
    </div>
  );
}
